/**
 * Razizo Reviews · product-reviews.js
 * Place in: assets/product-reviews.js
 *
 * Architecture:
 *  - Reviews fetched from Google Sheets via Apps Script
 *  - Submissions posted to Apps Script → stored in Sheet → awaits approval
 *  - Media converted to base64 and stored in Drive via Apps Script
 *  - All state is in-memory; re-fetched on page load
 */

(function () {
  'use strict';

  /* ────────────────────────────────────────
     CONFIG  (injected by Liquid)
  ──────────────────────────────────────── */
  const CFG = window.RAZIZO_REVIEWS_CONFIG || {};
  const SCRIPT_URL    = CFG.googleScriptUrl || '';
  const PRODUCT_ID    = String(CFG.productId || '');
  const PER_PAGE      = CFG.reviewsPerPage  || 6;
  const MAX_FILES     = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB — keeps upload times fast and Apps Script quotas safe

  /* ────────────────────────────────────────
     STATE
  ──────────────────────────────────────── */
  let allReviews     = [];   // approved reviews for this product
  let filteredReviews = [];
  let page           = 1;
  let activeFilter   = 'all';
  let selectedRating = 0;
  let pendingFiles   = [];   // { file, dataUrl }
  let lightboxMedia  = [];
  let lightboxIdx    = 0;

  /* ────────────────────────────────────────
     DOM REFS
  ──────────────────────────────────────── */
  const $ = id => document.getElementById(id);

  const elList        = $('rzv-list');
  const elEmpty       = $('rzv-empty');
  const elAvgScore    = $('rzv-avg-score');
  const elAvgStars    = $('rzv-avg-stars');
  const elTotalCount  = $('rzv-total-count');
  const elOverlay     = $('rzv-overlay');
  const elModal       = $('rzv-modal');
  const elOpenForm    = $('rzv-open-form');
  const elCloseForm   = $('rzv-close-form');
  const elCancel      = $('rzv-cancel');
  const elSubmit      = $('rzv-submit');
  const elSubmitText  = $('rzv-submit-text');
  const elSubmitSpinner = $('rzv-submit-spinner');
  const elError       = $('rzv-error');
  const elRatingVal   = $('rzv-rating-val');
  const elName        = $('rzv-name');
  const elTitle       = $('rzv-title');
  const elBody        = $('rzv-body');
  const elChars       = $('rzv-chars');
  const elMediaInput  = $('rzv-media-input');
  const elUploadArea  = $('rzv-upload-area');
  const elUploadPrompt = $('rzv-upload-prompt');
  const elPreviews    = $('rzv-previews');
  const elToast       = $('rzv-toast');
  const elLoadMoreWrap = $('rzv-load-more-wrap');
  const elLoadMore    = $('rzv-load-more');
  const elLightbox    = $('rzv-lightbox');
  const elLbContent   = $('rzv-lb-content');
  const elLbClose     = $('rzv-lb-close');
  const elLbPrev      = $('rzv-lb-prev');
  const elLbNext      = $('rzv-lb-next');
  const elStarPicker  = $('rzv-star-picker');

  /* ────────────────────────────────────────
     UTILITIES
  ──────────────────────────────────────── */
  function starsHTML(rating, size) {
    const cls = size === 'lg' ? 'rzv-stars rzv-stars--lg' : 'rzv-stars';
    let html = `<div class="${cls}" aria-label="${rating} out of 5 stars">`;
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(rating)) {
        html += `<span class="rzv-star rzv-star--filled" aria-hidden="true">★</span>`;
      } else if (i - rating < 1 && i - rating > 0) {
        html += `<span class="rzv-star rzv-star--half" aria-hidden="true">★</span>`;
      } else {
        html += `<span class="rzv-star" aria-hidden="true">★</span>`;
      }
    }
    return html + '</div>';
  }

  function formatDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return iso; }
  }

  function escapeHTML(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function showError(msg) {
    elError.textContent = msg;
    elError.style.display = 'block';
  }

  function hideError() {
    elError.style.display = 'none';
  }

  function showToast() {
    elToast.classList.add('rzv-toast--visible');
    setTimeout(() => elToast.classList.remove('rzv-toast--visible'), 4000);
  }

  function setLoading(on) {
    elSubmit.disabled = on;
    elSubmitText.style.display = on ? 'none' : '';
    elSubmitSpinner.style.display = on ? '' : 'none';
  }

  function fileToBase64(file) {
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload  = () => res(r.result);
      r.onerror = () => rej(new Error('File read failed'));
      r.readAsDataURL(file);
    });
  }

  /* ────────────────────────────────────────
     RENDER SUMMARY BARS
  ──────────────────────────────────────── */
  function renderSummary(reviews) {
    const total = reviews.length;
    if (!total) {
      elAvgScore.textContent = '—';
      elAvgStars.innerHTML = starsHTML(0);
      elTotalCount.textContent = '0 reviews';
      for (let s = 1; s <= 5; s++) {
        const bar = $(`rzv-bar-${s}`);
        const pct = $(`rzv-pct-${s}`);
        if (bar) bar.style.width = '0%';
        if (pct) pct.textContent = '0%';
      }
      return;
    }
    const sum = reviews.reduce((a, r) => a + Number(r.rating), 0);
    const avg = (sum / total).toFixed(1);
    elAvgScore.textContent = avg;
    elAvgStars.innerHTML = starsHTML(parseFloat(avg), 'lg');
    elTotalCount.textContent = `${total} review${total !== 1 ? 's' : ''}`;

    /* ── Push live data up to the mob-rating-bar at top of product page ── */
    const mrbScore       = document.getElementById('mrb-score-live');
    const mrbReviewCount = document.getElementById('mrb-review-count-live');
    if (mrbScore)       mrbScore.textContent = avg;
    if (mrbReviewCount) mrbReviewCount.textContent = `${total}+ reviews`;

    for (let s = 1; s <= 5; s++) {
      const count = reviews.filter(r => Number(r.rating) === s).length;
      const pctVal = total ? Math.round((count / total) * 100) : 0;
      const bar = $(`rzv-bar-${s}`);
      const pct = $(`rzv-pct-${s}`);
      if (bar) bar.style.width = pctVal + '%';
      if (pct) pct.textContent = pctVal + '%';
    }
  }

  /* ────────────────────────────────────────
     RENDER CARD
  ──────────────────────────────────────── */
  function renderCard(review) {
    const mediaArr = Array.isArray(review.media) ? review.media : (review.media ? [review.media] : []);
    let mediaHTML = '';
    if (mediaArr.length) {
      mediaHTML = '<div class="rzv-card__media">';
      mediaArr.forEach((url, idx) => {
        const isVideo = /\.(mp4|webm|mov|avi)/i.test(url) || url.includes('video');
        if (isVideo) {
          mediaHTML += `
            <div class="rzv-media-thumb" data-media-idx="${idx}" data-review-id="${review.id}" role="button" tabindex="0" aria-label="View video">
              <video src="${escapeHTML(url)}" muted preload="metadata" playsinline></video>
              <div class="rzv-media-thumb__play">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
              </div>
            </div>`;
        } else {
          mediaHTML += `
            <div class="rzv-media-thumb" data-media-idx="${idx}" data-review-id="${review.id}" role="button" tabindex="0" aria-label="View image">
              <img src="${escapeHTML(url)}" alt="Review photo" loading="lazy">
            </div>`;
        }
      });
      mediaHTML += '</div>';
    }

    const titleHTML = review.title
      ? `<p class="rzv-card__title">${escapeHTML(review.title)}</p>` : '';

    const verifiedHTML = review.verified
      ? `<span class="rzv-verified">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6 9 17l-5-5"/></svg>
          Verified
        </span>` : '';

    return `
      <article class="rzv-card" data-rating="${review.rating}" data-id="${review.id}">
        <div class="rzv-card__top">
          <div class="rzv-card__meta">
            <span class="rzv-card__author">${escapeHTML(review.name)}</span>
            <span class="rzv-card__date">${formatDate(review.date)}</span>
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
            ${starsHTML(Number(review.rating))}
            ${verifiedHTML}
          </div>
        </div>
        ${titleHTML}
        <p class="rzv-card__body">${escapeHTML(review.body)}</p>
        ${mediaHTML}
      </article>`;
  }

  /* ────────────────────────────────────────
     RENDER LIST
  ──────────────────────────────────────── */
  function renderList() {
    const visible = filteredReviews.slice(0, page * PER_PAGE);
    const cards   = visible.map(renderCard).join('');

    // Remove old cards (keep empty state node)
    Array.from(elList.querySelectorAll('.rzv-card')).forEach(c => c.remove());

    if (filteredReviews.length === 0) {
      elEmpty.style.display = '';
      elLoadMoreWrap.style.display = 'none';
    } else {
      elEmpty.style.display = 'none';
      elList.insertAdjacentHTML('beforeend', cards);
      elLoadMoreWrap.style.display = filteredReviews.length > visible.length ? '' : 'none';
    }

    // Attach lightbox triggers
    elList.querySelectorAll('.rzv-media-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => openLightboxFromThumb(thumb));
      thumb.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') openLightboxFromThumb(thumb); });
    });
  }

  /* ────────────────────────────────────────
     FILTERING
  ──────────────────────────────────────── */
  function applyFilter(filter) {
    activeFilter = filter;
    page = 1;
    filteredReviews = filter === 'all'
      ? [...allReviews]
      : allReviews.filter(r => String(r.rating) === filter);

    document.querySelectorAll('.rzv-filter').forEach(btn => {
      btn.classList.toggle('rzv-filter--active', btn.dataset.filter === filter);
    });
    renderList();
  }

  document.querySelectorAll('.rzv-filter').forEach(btn => {
    btn.addEventListener('click', () => applyFilter(btn.dataset.filter));
  });

  // Clicking a bar row also filters
  document.querySelectorAll('.rzv-bar-row').forEach(row => {
    row.addEventListener('click', () => applyFilter(row.dataset.star));
  });

  /* ────────────────────────────────────────
     LOAD MORE
  ──────────────────────────────────────── */
  if (elLoadMore) {
    elLoadMore.addEventListener('click', () => {
      page++;
      renderList();
    });
  }

  /* ────────────────────────────────────────
     MODAL OPEN / CLOSE
  ──────────────────────────────────────── */
  function openModal() {
    elOverlay.classList.add('rzv-overlay--visible');
    document.body.style.overflow = 'hidden';

    // Double rAF: ensures browser paints element at hidden state before
    // starting the transition (required for visibility+opacity transitions)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        elModal.classList.add('rzv-modal--visible');
        setTimeout(() => elName.focus(), 50);
      });
    });
  }

  function closeModal() {
    elOverlay.classList.remove('rzv-overlay--visible');
    elModal.classList.remove('rzv-modal--visible');
    document.body.style.overflow = '';
    resetForm();
  }

  elOpenForm.addEventListener('click', openModal);
  elCloseForm.addEventListener('click', closeModal);
  elCancel.addEventListener('click', closeModal);
  elOverlay.addEventListener('click', closeModal);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      closeModal();
      closeLightbox();
    }
  });

  /* ────────────────────────────────────────
     STAR PICKER
  ──────────────────────────────────────── */
  const starBtns = elStarPicker.querySelectorAll('.rzv-star-pick');

  function updatePickerUI(hovered) {
    starBtns.forEach((btn, i) => {
      const val = i + 1;
      btn.classList.toggle('rzv-star-pick--selected', val <= selectedRating && hovered === null);
      btn.classList.toggle('rzv-star-pick--hover',    hovered !== null && val <= hovered);
    });
  }

  starBtns.forEach((btn, i) => {
    btn.addEventListener('mouseenter', () => updatePickerUI(i + 1));
    btn.addEventListener('mouseleave', () => updatePickerUI(null));
    btn.addEventListener('click', () => {
      selectedRating = i + 1;
      elRatingVal.value = selectedRating;
      updatePickerUI(null);
    });
  });

  /* ────────────────────────────────────────
     CHARACTER COUNT
  ──────────────────────────────────────── */
  elBody.addEventListener('input', () => {
    elChars.textContent = elBody.value.length;
  });

  /* ────────────────────────────────────────
     FILE UPLOAD
  ──────────────────────────────────────── */
  elUploadArea.addEventListener('click', e => {
    if (!e.target.closest('.rzv-preview-item__remove')) elMediaInput.click();
  });

  elUploadArea.addEventListener('dragover', e => {
    e.preventDefault();
    elUploadArea.classList.add('rzv-upload-area--drag');
  });
  elUploadArea.addEventListener('dragleave', () => elUploadArea.classList.remove('rzv-upload-area--drag'));
  elUploadArea.addEventListener('drop', e => {
    e.preventDefault();
    elUploadArea.classList.remove('rzv-upload-area--drag');
    handleFiles(Array.from(e.dataTransfer.files));
  });

  elMediaInput.addEventListener('change', () => handleFiles(Array.from(elMediaInput.files)));

  async function handleFiles(files) {
    hideError();
    const remaining = MAX_FILES - pendingFiles.length;
    const toAdd = files.slice(0, remaining);

    for (const file of toAdd) {
      if (file.size > MAX_FILE_SIZE) {
        showError(`"${file.name}" is over 5MB. Please choose a smaller file.`);
        continue;
      }
      try {
        const dataUrl = await fileToBase64(file);
        pendingFiles.push({ file, dataUrl });
      } catch {
        showError('Could not read one of your files. Please try again.');
      }
    }
    renderPreviews();
  }

  function renderPreviews() {
    elPreviews.innerHTML = '';
    elUploadPrompt.style.display = pendingFiles.length ? 'none' : '';

    pendingFiles.forEach((item, idx) => {
      const div = document.createElement('div');
      div.className = 'rzv-preview-item';

      const isVideo = item.file.type.startsWith('video/');
      const media = document.createElement(isVideo ? 'video' : 'img');
      media.src = item.dataUrl;
      if (isVideo) { media.muted = true; media.preload = 'metadata'; }
      div.appendChild(media);

      const rmBtn = document.createElement('button');
      rmBtn.className = 'rzv-preview-item__remove';
      rmBtn.innerHTML = '×';
      rmBtn.setAttribute('aria-label', 'Remove file');
      rmBtn.addEventListener('click', e => {
        e.stopPropagation();
        pendingFiles.splice(idx, 1);
        renderPreviews();
      });
      div.appendChild(rmBtn);
      elPreviews.appendChild(div);
    });
  }

  /* ────────────────────────────────────────
     FORM RESET
  ──────────────────────────────────────── */
  function resetForm() {
    selectedRating = 0;
    elRatingVal.value = 0;
    updatePickerUI(null);
    elName.value  = '';
    elTitle.value = '';
    elBody.value  = '';
    elChars.textContent = '0';
    pendingFiles  = [];
    elMediaInput.value = '';
    renderPreviews();
    hideError();
    setLoading(false);
  }

  /* ────────────────────────────────────────
     FORM VALIDATION
  ──────────────────────────────────────── */
  function validate() {
    if (selectedRating === 0)  { showError('Please select a star rating.'); return false; }
    if (!elName.value.trim())  { showError('Please enter your name.');       return false; }
    if (!elBody.value.trim())  { showError('Please write your review.');     return false; }
    return true;
  }

  /* ────────────────────────────────────────
     SUBMIT REVIEW
  ──────────────────────────────────────── */
  elSubmit.addEventListener('click', async () => {
    hideError();
    if (!validate()) return;

    if (!SCRIPT_URL) {
      showError('Review submission is not configured yet. Please contact the store owner.');
      return;
    }

    setLoading(true);

    try {
      // Convert media to base64 array for Apps Script
      const mediaData = pendingFiles.map(f => ({
        name:     f.file.name,
        type:     f.file.type,
        dataUrl:  f.dataUrl
      }));

      const payload = {
        action:    'submitReview',
        productId: PRODUCT_ID,
        rating:    selectedRating,
        name:      elName.value.trim(),
        title:     elTitle.value.trim(),
        body:      elBody.value.trim(),
        media:     mediaData,   // base64 files uploaded to R2 by Worker
        date:      new Date().toISOString()
      };

      // Cloudflare Worker requires application/json
      const resp = await fetch(SCRIPT_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload)
      });

      const result = await resp.json();

      if (result.status === 'ok') {
        closeModal();
        showToast();
      } else {
        showError(result.message || 'Something went wrong. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Review submit error:', err);
      showError('Could not submit your review. Please check your connection and try again.');
      setLoading(false);
    }
  });

  /* ────────────────────────────────────────
     FETCH REVIEWS
  ──────────────────────────────────────── */
  async function fetchReviews() {
    if (!SCRIPT_URL) return;

    try {
      const url = `${SCRIPT_URL}?action=getReviews&productId=${encodeURIComponent(PRODUCT_ID)}`;
      const resp = await fetch(url);
      const data = await resp.json();

      if (data.status === 'ok' && Array.isArray(data.reviews)) {
        allReviews = data.reviews.sort((a, b) => new Date(b.date) - new Date(a.date));
        renderSummary(allReviews);
        applyFilter(activeFilter);
      }
    } catch (err) {
      console.error('Fetch reviews error:', err);
    }
  }

  /* ────────────────────────────────────────
     LIGHTBOX
  ──────────────────────────────────────── */
  function openLightboxFromThumb(thumb) {
    const reviewId = thumb.dataset.reviewId;
    const mediaIdx = parseInt(thumb.dataset.mediaIdx, 10);
    const review   = allReviews.find(r => String(r.id) === String(reviewId));
    if (!review) return;

    lightboxMedia = Array.isArray(review.media) ? review.media : [review.media];
    lightboxIdx   = mediaIdx;
    renderLightboxMedia();
    // Use CSS class instead of inline style so display:none !important in CSS is respected
    elLightbox.classList.add('rzv-lightbox--visible');
    document.body.style.overflow = 'hidden';
  }

  function renderLightboxMedia() {
    const url = lightboxMedia[lightboxIdx];
    const isVideo = /\.(mp4|webm|mov|avi)/i.test(url) || url.includes('video');
    elLbContent.innerHTML = isVideo
      ? `<video src="${escapeHTML(url)}" controls autoplay playsinline style="max-width:90vw;max-height:90vh;border-radius:8px;"></video>`
      : `<img src="${escapeHTML(url)}" alt="Review media" style="max-width:90vw;max-height:90vh;border-radius:8px;">`;

    elLbPrev.style.display = lightboxMedia.length > 1 ? '' : 'none';
    elLbNext.style.display = lightboxMedia.length > 1 ? '' : 'none';
  }

  function closeLightbox() {
    elLightbox.classList.remove('rzv-lightbox--visible');
    document.body.style.overflow = '';
    elLbContent.innerHTML = '';
  }

  elLbClose.addEventListener('click', closeLightbox);
  elLightbox.addEventListener('click', e => { if (e.target === elLightbox) closeLightbox(); });

  elLbPrev.addEventListener('click', () => {
    lightboxIdx = (lightboxIdx - 1 + lightboxMedia.length) % lightboxMedia.length;
    renderLightboxMedia();
  });

  elLbNext.addEventListener('click', () => {
    lightboxIdx = (lightboxIdx + 1) % lightboxMedia.length;
    renderLightboxMedia();
  });

  document.addEventListener('keydown', e => {
    if (!elLightbox.classList.contains('rzv-lightbox--visible')) return;
    if (e.key === 'ArrowLeft')  elLbPrev.click();
    if (e.key === 'ArrowRight') elLbNext.click();
  });

  /* ────────────────────────────────────────
     INIT
     UI (modal, stars, filters) is always initialised immediately.
     Data fetch is deferred until the tab is visible to avoid
     unnecessary network calls on page load.
  ──────────────────────────────────────── */

  // Expose fetch function globally so razizo-custom.js can call it when tab opens
  window.rzvFetchReviews = fetchReviews;

  // Check if our section is inside a hidden tab panel
  const rzvSection = document.getElementById('rzv-reviews');
  const rzvPanel   = rzvSection ? rzvSection.closest('.tab-panel') : null;
  const rzvVisible = !rzvPanel || rzvPanel.classList.contains('active');

  if (rzvVisible) {
    // Visible on load — fetch immediately
    fetchReviews();
  }
  // If hidden, razizo-custom.js will call window.rzvFetchReviews() when tab opens.
  // The modal and all UI are already initialised above regardless of visibility.

})();